import React, { useEffect, useState } from "react";

const safeStr = (v) => String(v || "").trim();

const isPdfFile = (f) => {
  const ct = safeStr(f?.contentType).toLowerCase();
  if (ct.includes("pdf")) return true;
  const name = safeStr(f?.originalName).toLowerCase();
  return name.endsWith(".pdf");
};

const isImageFile = (f) => {
  const ct = safeStr(f?.contentType).toLowerCase();
  if (ct.startsWith("image/")) return true;
  const name = safeStr(f?.originalName).toLowerCase();
  return (
    name.endsWith(".png") ||
    name.endsWith(".jpg") ||
    name.endsWith(".jpeg") ||
    name.endsWith(".webp") ||
    name.endsWith(".gif")
  );
};

const makePdfEmbedSrc = (downloadURL) => {
  const raw = safeStr(downloadURL);
  if (!raw) return "";
  if (raw.includes("#")) return raw;
  return `${raw}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0&messages=0`;
};

export default function DocumentPreviewModal({
  open,
  file,
  title,
  onClose,
  onDownload, // optional
  floatingActions,
  pdfRenderer: PdfRenderer,
  loading = false,
  loadingText = "Loading...",
  showOpenInNewTab = true,
  className = "docModalOverlayPreview",
  shellHeight = 720,
  style,
}) {
  const relatedFiles =
    Array.isArray(file?.relatedApprovedFiles) && file.relatedApprovedFiles.length
      ? file.relatedApprovedFiles
      : [];
  const filesToPreview = relatedFiles.length > 1 ? relatedFiles : [file];

  function fileUrl(f) {
    return safeStr(f?.downloadURL || f?.downloadUrl || f?.url);
  }

  function previewMediaKey(f, index) {
    const url = fileUrl(f);
    if (!url || (!isPdfFile(f) && !isImageFile(f))) return "";
    return `${index}:${url}`;
  }

  const mediaKeys =
    open && !loading
      ? filesToPreview
          .map((previewFile, index) => previewMediaKey(previewFile, index))
          .filter(Boolean)
      : [];
  const mediaKeySignature = mediaKeys.join("|");
  const [loadedMediaKeys, setLoadedMediaKeys] = useState(() => new Set());

  useEffect(() => {
    setLoadedMediaKeys(new Set());
  }, [open, loading, mediaKeySignature]);

  const markPreviewMediaLoaded = (key) => {
    if (!key) return;
    setLoadedMediaKeys((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  const hasPreviewMedia = !loading && mediaKeys.length > 0;
  const previewMediaReady =
    !hasPreviewMedia || mediaKeys.every((key) => loadedMediaKeys.has(key));
  const showLoader = loading || (hasPreviewMedia && !previewMediaReady);

  if (!open) return null;

  function renderPreviewFile(f, index) {
    const url = fileUrl(f);
    const displayName = safeStr(f?.originalName) || `Document ${index + 1}`;
    const showFileHeader = filesToPreview.length > 1;
    const mediaKey = previewMediaKey(f, index);

    return (
      <div
        key={safeStr(f?.objectPath) || `${displayName}_${index}`}
        className={showFileHeader ? "docPreviewStackItem" : ""}
      >
        {showFileHeader ? (
          <div className="docPreviewFileHeader">
            <span className="docPreviewFileIndex">{index + 1}</span>
          </div>
        ) : null}

        {!url ? (
          <div className="alert alert-warning">No download URL available.</div>
        ) : isPdfFile(f) ? (
          <div className="docPreviewWrap">
            {PdfRenderer ? <PdfRenderer key={url} file={url} onReady={() => markPreviewMediaLoaded(mediaKey)} /> : <iframe
              title={displayName}
              src={makePdfEmbedSrc(url)}
              className="docPdfFrame"
              onLoad={() => markPreviewMediaLoaded(mediaKey)}
            />}
            {showOpenInNewTab || onDownload ? (
              <div className="docPreviewActions">
                {showOpenInNewTab ? (
                <a
                  className="btn btn-sm btn-outline-secondary docBtnRound"
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open in new tab
                </a>
              ) : null}
              {onDownload ? (
                <button
                  className="btn btn-sm btn-outline-secondary docBtnRound"
                  onClick={onDownload}
                >
                  Download
                </button>
              ) : null}
              </div>
            ) : null}
          </div>
        ) : isImageFile(f) ? (
          <div className="docImgPreviewFrame">
            <img
              src={url}
              alt={displayName}
              className="docImgPreview isFull"
              onLoad={() => markPreviewMediaLoaded(mediaKey)}
            />
          </div>
        ) : (
          <div>
            <div className="alert alert-secondary">
              Preview not supported for this file type.
            </div>
            <a
              className="btn btn-outline-primary docBtnRound"
              href={url}
              target="_blank"
              rel="noreferrer"
            >
              Open
            </a>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`docModalOverlay ${className || ""}`}
      onMouseDown={onClose}
      style={{
        "--doc-preview-stack-height": "774px",
        ...(style || {}),
      }}
    >
      <div className="docModalStack" onMouseDown={(e) => e.stopPropagation()}>
        <div
          className="docModalShell"
          style={shellHeight ? { height: shellHeight } : undefined}
        >
          <div className="docModalHeader">
            <div className="docModalTitle">
              {title || file?.originalName || "Preview"}
            </div>
            <button
              type="button"
              className="docModalCloseBtn"
              onClick={onClose}
              aria-label="Close preview"
            >
              <i className="bi bi-x-lg" aria-hidden="true" />
            </button>
          </div>

          <div className={`docModalBody${PdfRenderer ? " docModalBody--pdfPages" : ""}`}>
            <div className="docPreviewContentWrap">
              {!loading
                ? filesToPreview.map((previewFile, index) =>
                    renderPreviewFile(previewFile, index)
                  )
                : null}
              {showLoader ? (
                <div className="docPreviewLoader">
                  <div>
                    <div className="appDotsLoader" aria-hidden="true" />
                    <div
                      className="appTextLoader"
                      data-text={loadingText}
                      aria-label={loadingText}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
          {floatingActions ? (
            <div className="docModalFloatingActions" onMouseDown={(e) => e.stopPropagation()}>
              {floatingActions}
            </div>
          ) : null}
        </div>
              </div>
    </div>
  );
}
